import * as React from "react";
import { Chip, Skeleton, Tab, TabProps, Tabs, TabsProps } from "@mui/material";
import { useLanguage } from "./Language";

export interface DatabaseTabsProps extends TabsProps {
  useBritannica: boolean;
  useGale: boolean;
  useEbsco: boolean;
  useNewsBank: boolean;
  useWorldBook: boolean;
  reservesEnabled: boolean;
  pacCount: number | null;
  britannicaCount?: number;
  galeCount?: number;
  ebscoCount?: number;
  newsBankCount?: number;
  worldBookCount?: number;
  reservesCount?: number;
}

export enum DatabaseTabKey {
  ls2pac = "ls2pac",
  britannica = "britannica",
  newsbank = "newsbank",
  ebsco = "ebsco",
  gale = "gale",
  reserves = "reserves",
  worldbook = "worldbook",
}

interface VerticalTabWithCountProps extends TabProps {
  count: number;
  hideCount?: boolean;
  active: boolean;
}

export const VerticalTab: React.FC<TabProps> = (props) => {
  return (
    <Tab
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
      }}
      {...props}
    ></Tab>
  );
}

export const VerticalLinkTab: React.FC<TabProps<"a">> = (props) => {
  return (
    <Tab
      component="a"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
      }}
      {...props}
    ></Tab>
  );
}

const VerticalTabWithCount: React.FC<VerticalTabWithCountProps> = ({
  label,
  count,
  active,
  value,
  hideCount,
  ...props
}) => {
  return (
    <Tab
      value={value}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
      }}
      label={
        <React.Fragment>
          <span>{label}</span>
          {!hideCount && count !== null && (
            <Chip
              size="small"
              variant={active ? "filled" : "outlined"}
              color={active ? "secondary" : "default"}
              label={new Intl.NumberFormat().format(count)}
            />
          )}
          {!hideCount && count === null && (
            <Skeleton variant="circular">
              <Chip size="small" label={0} />
            </Skeleton>
          )}
        </React.Fragment>
      }
      {...props}
    ></Tab>
  );
};

export const DatabaseTabs: React.FC<DatabaseTabsProps> = (props) => {
  const {
    value,
    useBritannica,
    useGale,
    useEbsco,
    useNewsBank,
    useWorldBook,
    reservesEnabled,
    pacCount,
    britannicaCount,
    galeCount,
    ebscoCount,
    newsBankCount,
    worldBookCount,
    reservesCount,
    ...remainingProps
  } = props;
  const { translate } = useLanguage();

  return (
    <Tabs
      value={value}
      sx={{ maxWidth: "320px" }}
      {...remainingProps}
      textColor="secondary"
      orientation="vertical"
      indicatorColor="secondary"
      variant="scrollable"
      aria-label="Database Selection"
    >
      <VerticalTabWithCount
        active={value === DatabaseTabKey.ls2pac}
        value={DatabaseTabKey.ls2pac}
        label={translate("ls2pacDatabaseTabName")}
        count={pacCount}
      />
      {useEbsco && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.ebsco}
          value={DatabaseTabKey.ebsco}
          count={ebscoCount}
          label={translate("ebscoDatabaseTabName")}
        />
      )}
      {useNewsBank && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.newsbank}
          value={DatabaseTabKey.newsbank}
          count={newsBankCount}
          label={translate("newsbankDatabaseTabName")}
        />
      )}
      {useBritannica && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.britannica}
          value={DatabaseTabKey.britannica}
          count={britannicaCount}
          label={translate("britannicaDatabaseTabName")}
        />
      )}
      {useGale && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.gale}
          value={DatabaseTabKey.gale}
          count={galeCount}
          label={translate("galeDatabaseTabName")}
        />
      )}
      {useWorldBook && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.worldbook}
          value={DatabaseTabKey.worldbook}
          count={worldBookCount}
          label={translate("worldbookDatabaseTabName")}
        />
      )}
      {reservesEnabled && (
        <VerticalTabWithCount
          active={value === DatabaseTabKey.reserves}
          value={DatabaseTabKey.reserves}
          count={reservesCount}
          hideCount={
            reservesCount === null && value !== DatabaseTabKey.reserves
          }
          label={translate("academicReserves")}
        />
      )}
    </Tabs>
  );
};
